import { Input } from "@egaranti/components";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@egaranti/components";
import { Popover, PopoverContent, PopoverTrigger } from "@egaranti/components";
import { Button } from "@egaranti/components";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductService from "@/services/productService";

import useProductsStore from "@/stores/useProductsStore";

import { Loader2, Search, SlidersHorizontal, X } from "lucide-react";

const ProductFilterBar = () => {
  const { t } = useTranslation();
  const { filters, getProducts, setFilters, setFiltersAndFetch, clearAllFilters } =
    useProductsStore();

  // Local search state
  const [searchValue, setSearchValue] = useState(filters.query || "");

  // Category data
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  // Loading states
  const [isLoadingMain, setIsLoadingMain] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingSubCategories, setIsLoadingSubCategories] = useState(false);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  // Advanced filters popover
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Local advanced filter states
  const [localPriceMin, setLocalPriceMin] = useState(filters.priceMin || "");
  const [localPriceMax, setLocalPriceMax] = useState(filters.priceMax || "");
  const [localDateStart, setLocalDateStart] = useState(
    filters.createdAtStart || "",
  );
  const [localDateEnd, setLocalDateEnd] = useState(filters.createdAtEnd || "");

  // Load main categories on mount
  // EPS-4850 B-9 fix: ProductService response shape inconsistent — service bazı
  // çağrılarda raw axios response (data.data), bazılarında array dönüyor. Edit page
  // defansif okuyor; filter bar'da da aynı yaklaşım — `data.map is not a function`
  // ile bütün filter bar'ın çökmesini engelle.
  const normalizeList = (res) => res?.data?.data || res?.data || [];

  useEffect(() => {
    let cancelled = false;
    const loadMainCategories = async () => {
      setIsLoadingMain(true);
      try {
        const res = await ProductService.getMainCategories();
        if (!cancelled) setMainCategories(normalizeList(res));
      } catch (error) {
        if (!cancelled) console.error("Failed to load main categories:", error);
      } finally {
        if (!cancelled) setIsLoadingMain(false);
      }
    };
    loadMainCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync local states with filters
  useEffect(() => {
    setSearchValue(filters.query || "");
    setLocalPriceMin(filters.priceMin || "");
    setLocalPriceMax(filters.priceMax || "");
    setLocalDateStart(filters.createdAtStart || "");
    setLocalDateEnd(filters.createdAtEnd || "");
  }, [filters]);

  // Load dependent categories when filters change
  useEffect(() => {
    const loadDependentData = async () => {
      if (filters.mainCategoryId) {
        setIsLoadingCategories(true);
        try {
          const { data } = await ProductService.getCategories(
            filters.mainCategoryId,
          );
          setCategories(data);
        } catch (error) {
          console.error("Failed to load categories:", error);
        } finally {
          setIsLoadingCategories(false);
        }
      } else {
        setCategories([]);
      }

      if (filters.categoryId) {
        setIsLoadingSubCategories(true);
        try {
          const { data } = await ProductService.getSubCategories(
            filters.categoryId,
          );
          setSubCategories(data);
        } catch (error) {
          console.error("Failed to load subcategories:", error);
        } finally {
          setIsLoadingSubCategories(false);
        }
      } else {
        setSubCategories([]);
      }

      if (filters.subCategoryId) {
        setIsLoadingBrands(true);
        try {
          const { data } = await ProductService.getSubCategoryBrands(
            filters.subCategoryId,
          );
          setBrands(data);
        } catch (error) {
          console.error("Failed to load brands:", error);
        } finally {
          setIsLoadingBrands(false);
        }
      } else {
        setBrands([]);
      }
    };

    loadDependentData();
  }, [filters.mainCategoryId, filters.categoryId, filters.subCategoryId]);

  // Handlers
  // EPS-4850 B-4 fix: setFilters + getProducts race'i için store atomic action.
  const handleSearch = useCallback(() => {
    setFiltersAndFetch({ ...filters, query: searchValue, page: 1 });
  }, [searchValue, filters, setFiltersAndFetch]);

  // Debounced search — fire 400ms after user stops typing, but only when the
  // local value diverges from the applied filter (avoids re-firing on mount).
  const debounceRef = useRef(null);
  const isFirstRunRef = useRef(true);
  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      return;
    }
    if (searchValue === (filters.query || "")) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFiltersAndFetch({ ...filters, query: searchValue, page: 1 });
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleMainCategoryChange = useCallback(
    async (value) => {
      const mainCategoryId = value === "all" ? "" : value;
      setFiltersAndFetch({
        ...filters,
        mainCategoryId,
        categoryId: "",
        subCategoryId: "",
        subCategoryBrandId: "",
        page: 1,
      });
    },
    [filters, setFiltersAndFetch],
  );

  const handleCategoryChange = useCallback(
    async (value) => {
      const categoryId = value === "all" ? "" : value;
      setFiltersAndFetch({
        ...filters,
        categoryId,
        subCategoryId: "",
        subCategoryBrandId: "",
        page: 1,
      });
    },
    [filters, setFiltersAndFetch],
  );

  const handleSubCategoryChange = useCallback(
    async (value) => {
      const subCategoryId = value === "all" ? "" : value;
      setFiltersAndFetch({
        ...filters,
        subCategoryId,
        subCategoryBrandId: "",
        page: 1,
      });
    },
    [filters, setFiltersAndFetch],
  );

  const handleBrandChange = useCallback(
    (value) => {
      const subCategoryBrandId = value === "all" ? "" : value;
      setFiltersAndFetch({
        ...filters,
        subCategoryBrandId,
        page: 1,
      });
    },
    [filters, setFiltersAndFetch],
  );

  const clearSearch = () => {
    setSearchValue("");
    setFiltersAndFetch({ ...filters, query: "", page: 1 });
  };

  // Apply advanced filters
  const applyAdvancedFilters = () => {
    setFiltersAndFetch({
      ...filters,
      priceMin: localPriceMin,
      priceMax: localPriceMax,
      createdAtStart: localDateStart,
      createdAtEnd: localDateEnd,
      page: 1,
    });
    setAdvancedOpen(false);
  };

  // Clear advanced filters
  const clearAdvancedFilters = () => {
    setLocalPriceMin("");
    setLocalPriceMax("");
    setLocalDateStart("");
    setLocalDateEnd("");
    setFiltersAndFetch({
      ...filters,
      priceMin: "",
      priceMax: "",
      createdAtStart: "",
      createdAtEnd: "",
      page: 1,
    });
    setAdvancedOpen(false);
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.query ||
    filters.mainCategoryId ||
    filters.categoryId ||
    filters.subCategoryId ||
    filters.subCategoryBrandId ||
    filters.priceMin ||
    filters.priceMax ||
    filters.createdAtStart ||
    filters.createdAtEnd;

  // Count advanced filters
  const advancedFilterCount = [
    filters.priceMin || filters.priceMax,
    filters.createdAtStart || filters.createdAtEnd,
  ].filter(Boolean).length;

  return (
    <div className="w-full rounded-t-lg border-b bg-white">
      {/* Main Filter Row */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-4">
        {/* Search Input */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9AA4B2]" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={t("productsPage.searchProducts", "Ürün ara...")}
            className="h-10 w-full rounded-lg border border-[#E3E8EF] bg-white pl-10 pr-9 text-sm text-[#111729] placeholder-[#9AA4B2] transition-colors focus:border-[#0049E6] focus:outline-none focus:ring-1 focus:ring-[#0049E6]"
          />
          {searchValue && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA4B2] hover:text-[#364152]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-[#E3E8EF]" />

        {/* Main Category */}
        <Select
          value={filters.mainCategoryId || "all"}
          onValueChange={handleMainCategoryChange}
        >
          <SelectTrigger className="h-10 w-[160px] border-[#E3E8EF] bg-white">
            {isLoadingMain ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <SelectValue placeholder={t("productsPage.mainCategory")} />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all", "Tümü")}</SelectItem>
            {mainCategories.map((category) => (
              <SelectItem key={category.id} value={category.id.toString()}>
                {t(category.name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Category */}
        {filters.mainCategoryId && (
          <Select
            value={filters.categoryId || "all"}
            onValueChange={handleCategoryChange}
            disabled={isLoadingCategories}
          >
            <SelectTrigger className="h-10 w-[160px] border-[#E3E8EF] bg-white">
              {isLoadingCategories ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SelectValue placeholder={t("productsPage.category")} />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all", "Tümü")}</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id.toString()}>
                  {t(category.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Sub Category */}
        {filters.categoryId && (
          <Select
            value={filters.subCategoryId || "all"}
            onValueChange={handleSubCategoryChange}
            disabled={isLoadingSubCategories}
          >
            <SelectTrigger className="h-10 w-[160px] border-[#E3E8EF] bg-white">
              {isLoadingSubCategories ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SelectValue placeholder={t("productsPage.subCategory")} />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all", "Tümü")}</SelectItem>
              {subCategories.map((category) => (
                <SelectItem key={category.id} value={category.id.toString()}>
                  {t(category.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Brand */}
        {filters.subCategoryId && (
          <Select
            value={filters.subCategoryBrandId || "all"}
            onValueChange={handleBrandChange}
            disabled={isLoadingBrands}
          >
            <SelectTrigger className="h-10 w-[140px] border-[#E3E8EF] bg-white">
              {isLoadingBrands ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SelectValue placeholder={t("productsPage.brand")} />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all", "Tümü")}</SelectItem>
              {brands.map((brand) => (
                <SelectItem key={brand.id} value={brand.id.toString()}>
                  {brand.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Advanced Filters Popover */}
        <Popover open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="secondaryGray"
              className={`h-10 gap-2 border-[#E3E8EF] ${advancedFilterCount > 0 ? "border-[#0049E6] bg-[#EEF4FF] text-[#0049E6]" : ""}`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t("productsPage.advancedFilters", "Gelişmiş")}
              {advancedFilterCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0049E6] text-xs text-white">
                  {advancedFilterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          {/*
            EPS-5120 (6. bulgu) — "Gelişmiş" filtre penceresi ekrandan tasiyordu.

            Paylasilan PopoverContent'in temel genisligi `w-72` (288 px); burada
            `w-full` ile eziliyordu ve mutlak konumlandirilmis panoda bu deger
            kapsayan bloga gore cozulup 444 px'e cikiyordu. Replikada olculdu:
            390 px'lik ekranda pano 444 px, sag kenari 444 -> ekranin 54 px
            disinda; "Uygula" dugmesi 37 px, "Max" fiyat ve bitis tarihi
            alanlari da 37 px disarida kaliyor.

            Cozum: masaustundeki 444 px aynen korunuyor, ancak ekrandan tasmamasi
            icin `max-w` ile viewport'a baglaniyor (iki yanda 1rem bosluk).
          */}
          <PopoverContent
            className="w-[444px] max-w-[calc(100vw-2rem)] bg-white p-4"
            align="end"
          >
            <div className="space-y-4">
              <h4 className="font-medium text-[#111729]">
                {t("productsPage.advancedFiltersTitle", "Gelişmiş Filtreler")}
              </h4>

              {/* Price Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#364152]">
                  {t("productsPage.priceRange", "Fiyat Aralığı")}
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder={t("common.min", "Min")}
                    value={localPriceMin}
                    onChange={(e) => setLocalPriceMin(e.target.value)}
                    className="h-9"
                  />
                  <span className="text-[#9AA4B2]">-</span>
                  <Input
                    type="number"
                    placeholder={t("common.max", "Max")}
                    value={localPriceMax}
                    onChange={(e) => setLocalPriceMax(e.target.value)}
                    className="h-9"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#364152]">
                  {t("productsPage.dateRange", "Tarih Aralığı")}
                </label>
                <div className="flex items-center gap-2">
                  {/*
                    EPS-5120 (6. bulgu, ikinci tur) — `type="date"` girdisinin
                    dogal genisligi var ve `flex-1` tek basina onu kucultemiyor
                    (esnek ogelerin varsayilan `min-width: auto` degeri taban
                    olusturur). Pano ekrana sigdirildiktan sonra bile bitis
                    tarihi alani panonun sag kenarini 13 px asiyordu (olculen:
                    girdi sag 371, pano sag 358). `min-w-0` tabani kaldiriyor.
                  */}
                  <Input
                    type="date"
                    value={localDateStart}
                    onChange={(e) => setLocalDateStart(e.target.value)}
                    className="h-9 w-full min-w-0 flex-1"
                  />
                  <span className="text-[#9AA4B2]">-</span>
                  <Input
                    type="date"
                    value={localDateEnd}
                    onChange={(e) => setLocalDateEnd(e.target.value)}
                    className="h-9 w-full min-w-0 flex-1"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAdvancedFilters}
                  className="text-[#677389]"
                >
                  {t("common.clear", "Temizle")}
                </Button>
                <Button size="sm" onClick={applyAdvancedFilters}>
                  {t("common.apply", "Uygula")}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Clear All - Right aligned */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="ml-auto flex items-center gap-1 text-sm font-medium text-[#677389] hover:text-[#364152]"
          >
            <X className="h-4 w-4" />
            {t("common.clearAll", "Temizle")}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductFilterBar;
